import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getTenantUsersOptions } from '@/features/tenants/api/tenant-users';

import { useCreateUserToken } from '../../api/user-token';

export interface AddContactModalProps {
  tenantId: string;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({ tenantId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useCreateUserToken({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('successfully-invited-contact'));
        queryClient.invalidateQueries({ queryKey: getTenantUsersOptions(tenantId).queryKey });
        // TODO: send the token
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const schema = z.object({
    email: z.string().email(),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    mutate({
      ...data,
      tenantId,
      type: 'contact',
      role: 'client',
    });
    closeModal();
  };

  return (
    <ModalBase className="min-w-[360px]">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={form.control}
            render={({ field, formState }) => (
              <Input
                {...field}
                label={t('email')}
                type="email"
                errors={[formState.errors.email?.message || '']}
              />
            )}
          />
          <div className="mt-4 flex flex-row gap-2">
            <Button variant="contained" type="submit">
              {t('submit')}
            </Button>
            <Button variant="outline" onClick={closeModal}>
              {t('close')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </ModalBase>
  );
};
